














































import * as converter from 'color-convert'
import * as R from 'ramda'
import Vue from 'vue'
import { PATIENT_STATS_DETAILED_REPORT_TYPES } from '@/components/patient-stats/constants'
import { round } from '@/components/exercises/measure/filters'
import { withSingularOrPluralLabel } from '@/utils/labels'
import { InhalerTypes } from '@/jsonapi/CommonTypes'
import { INCLUDE_INHALERS } from '@/constants/app-variables'

export default Vue.extend({
  filters: {
    round,
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
    patientWithDetailedReports: {
      type: Object,
      default: null,
    },
  },
  computed: {
    awakeningsPerNight() {
      return R.pathOr(0, ['sleepReport', 'avgNumOfAwakeningsPerNight'], this.patientWithDetailedReports)
    },
    numOfNightInhalerUsages() {
      return R.pathOr(null, ['inhalersReport', 'nightInhalerUsages', 'length'], this.patientWithDetailedReports)
    },
    numOfAvgSteps() {
      return R.pathOr(0, ['activityReport', 'avgStepsCountPerDay'], this.patientWithDetailedReports)
    },
    numOfSedentaryHours() {
      return R.pathOr(null, ['activityReport', 'avgSedentaryTimePerDay'], this.patientWithDetailedReports)
    },
    avgAdherenceRate() {
      const rate = R.pathOr(null, ['inhalersReport', 'maintenanceAdherenceRatePerc'], this.patientWithDetailedReports)
      return rate && rate >= 0 ? rate + '%' : null
    },
    hasSignOfShortnessOfBreath() {
      return R.pathOr(false, ['inhalersReport', 'hasSignOfShortnessOfBreath'], this.patientWithDetailedReports)
    },
    controlledDays(): Array<{ contentNum: string; contentText: string; style: object }> {
      const result = []
      const controlledDays = R.pathOr(null, ['inhalersReport', 'controlledDays'], this.patientWithDetailedReports)
      const uncontrolledDays = R.pathOr(null, ['inhalersReport', 'uncontrolledDays'], this.patientWithDetailedReports)
      const maxStrLength = Math.max(String(controlledDays).length, String(uncontrolledDays).length, 0)
      const style = {
        minWidth: `${maxStrLength * 0.5}em`,
      }
      if (controlledDays) {
        result.push({
          contentNum: controlledDays,
          contentText: 'days controlled',
          style,
        })
      }
      if (uncontrolledDays) {
        result.push({
          contentNum: uncontrolledDays,
          contentText: 'days uncontrolled',
          style,
        })
      }
      return result
    },
    tabs() {
      return Object.values(PATIENT_STATS_DETAILED_REPORT_TYPES)
        .map(obj => {
          const colors = this.$vuetify.theme.themes.light
          const contents = []
          switch (obj.key) {
            case PATIENT_STATS_DETAILED_REPORT_TYPES.SLEEP.key:
              contents.push({
                contentNum: round(this.awakeningsPerNight),
                contentText: 'Awakenings/Night',
              })
              return {
                ...obj,
                contents,
                showBadge: this.numOfNightInhalerUsages != null && this.numOfNightInhalerUsages !== 0,
                badgeContent: `${withSingularOrPluralLabel(
                  this.numOfNightInhalerUsages,
                  'time',
                  'times'
                )} inhaler use at night`,
                badgeIcon: '$vuetify.icons.customInhaler',
                badgeColor: colors.primary,
              }
            case PATIENT_STATS_DETAILED_REPORT_TYPES.ACTIVITY.key:
              contents.push({
                contentNum: round(this.numOfAvgSteps),
                contentText: 'Steps/Day',
              })
              return {
                ...obj,
                contents,
                showBadge: this.numOfSedentaryHours != null,
                badgeContent: `${withSingularOrPluralLabel(
                  this.numOfSedentaryHours,
                  'hour',
                  'hours'
                )} sedentary time per day`,
                badgeIcon: '$vuetify.icons.customChair',
                badgeColor: colors.primary,
              }
            case PATIENT_STATS_DETAILED_REPORT_TYPES.MAINTENANCE_INHALER.key:
              // Nursing homes won't include inhalers here.
              if (!INCLUDE_INHALERS) {
                return null
              } else if (this.noMaintenanceInhalerData) {
                contents.push({
                  contentNum: null,
                  contentText: 'No inhaler data',
                })
              } else if (this.avgAdherenceRate != null) {
                contents.push({
                  contentNum: this.avgAdherenceRate,
                  contentText: 'Adherence Rate',
                })
              }
              return {
                ...obj,
                contents,
                showBadge: false,
              }
            case PATIENT_STATS_DETAILED_REPORT_TYPES.RESCUE_INHALER.key:
              // Nursing homes won't include inhalers here.
              if (!INCLUDE_INHALERS) {
                return null
              } else if (this.noRescueInhalerData) {
                contents.push({
                  contentNum: null,
                  contentText: 'No inhaler data',
                })
              }
              return {
                ...obj,
                contents: this.noRescueInhalerData ? contents : this.controlledDays,
                showBadge: this.hasSignOfShortnessOfBreath,
                badgeContent: 'Signs of shortness of breath',
                badgeIcon: 'error',
                badgeColor: colors.red,
              }
            default:
              return obj
          }
        })
        .filter(x => !!x)
    },
    noMaintenanceInhalerData(): boolean {
      const inhalerUsages = R.pathOr([], ['inhalersReport', 'inhalerUsages'], this.patientWithDetailedReports)
      const mUsages = inhalerUsages.filter(usage => usage.type === InhalerTypes.Maintenance)
      const nUsages = R.pathOr([], ['inhalersReport', 'nightInhalerUsages'], this.patientWithDetailedReports).filter(
        usage => usage.type === InhalerTypes.Maintenance
      )
      const inhalerDurations = R.pathOr(
        [],
        ['inhalersReport', 'inhalerUsageDurations'],
        this.patientWithDetailedReports
      ).filter(usage => usage.type === InhalerTypes.Maintenance)
      return !mUsages.length && !nUsages.length && !inhalerDurations.length
    },
    noRescueInhalerData(): boolean {
      const inhalerUsages = R.pathOr([], ['inhalersReport', 'inhalerUsages'], this.patientWithDetailedReports)
      const rUsages = inhalerUsages.filter(usage => usage.type === InhalerTypes.Reliever)
      const nUsages = R.pathOr([], ['inhalersReport', 'nightInhalerUsages'], this.patientWithDetailedReports).filter(
        usage => usage.type === InhalerTypes.Reliever
      )
      const inhalerDurations = R.pathOr(
        [],
        ['inhalersReport', 'inhalerUsageDurations'],
        this.patientWithDetailedReports
      ).filter(usage => usage.type === InhalerTypes.Reliever)
      return !rUsages.length && !nUsages.length && !inhalerDurations.length
    },
  },
  methods: {
    getRgbaColor(color, alpha) {
      return `rgba(${converter.hex.rgb(color).join(',')}, ${alpha})`
    },
  },
})
